import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Gift, Power, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function CoachReferral() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ referrer_percent: "", referee_percent: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/coach/referral").then((r) => {
    setData(r.data);
    setForm({ referrer_percent: r.data.settings.referrer_percent, referee_percent: r.data.settings.referee_percent });
  });
  useEffect(() => { load(); }, []);

  const toggleActive = async () => {
    await api.patch("/coach/referral", { active: !data.settings.active });
    load(); toast.success(data.settings.active ? "Parrainage désactivé" : "Parrainage activé");
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/coach/referral", { referrer_percent: Number(form.referrer_percent), referee_percent: Number(form.referee_percent) });
      load(); toast.success("Règles enregistrées");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  if (!data) return <CoachLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></CoachLayout>;

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium flex items-center gap-2"><Gift size={20} strokeWidth={1.6} /> Parrainage</h1>
        <p className="mt-2 text-sm text-obsidian/50">Définis les récompenses, active le programme et suis les parrainages en cours. La récompense du parrain est débloquée au 1er paiement du filleul.</p>

        <div className="mt-6 bg-white border border-border p-6 max-w-3xl">
          <div className="flex items-center justify-between">
            <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">Règles de parrainage</div>
            <button data-testid="referral-toggle-active" onClick={toggleActive}
              className={`rounded-sm px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${data.settings.active ? "bg-sage/15 text-sage" : "border border-border text-obsidian/50"}`}>
              <Power size={13} /> {data.settings.active ? "Actif" : "Inactif"}
            </button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-obsidian/50">Récompense du parrain (% sur le prochain cycle)</label>
              <input data-testid="referral-referrer-percent" type="number" min="0" max="100" value={form.referrer_percent}
                onChange={(e) => setForm({ ...form, referrer_percent: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm tabnum focus:ring-1 focus:ring-sage focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-obsidian/50">Avantage du filleul (% sur le 1er cycle)</label>
              <input data-testid="referral-referee-percent" type="number" min="0" max="100" value={form.referee_percent}
                onChange={(e) => setForm({ ...form, referee_percent: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm tabnum focus:ring-1 focus:ring-sage focus:outline-none" />
            </div>
          </div>
          <button data-testid="referral-save" onClick={save} disabled={saving}
            className="mt-4 rounded-sm bg-obsidian text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer les règles
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Counter label="Total" value={data.counts.total} />
          <Counter label="En attente" value={data.counts.pending} />
          <Counter label="Récompensés" value={data.counts.rewarded} accent />
        </div>

        <div className="mt-6 overflow-x-auto no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
         <div className="min-w-[680px] border-t border-l border-border">
          <div className="grid grid-cols-12 bg-obsidian text-white text-[11px] tracking-[0.14em] uppercase">
            <div className="col-span-4 px-4 py-3 border-r border-white/10">Parrain</div>
            <div className="col-span-4 px-4 py-3 border-r border-white/10">Filleul</div>
            <div className="col-span-2 px-4 py-3 border-r border-white/10">Statut</div>
            <div className="col-span-2 px-4 py-3">Date</div>
          </div>
          {data.referrals.length === 0 && <div className="bg-white border-b border-r border-border px-4 py-5 text-sm text-obsidian/40">Aucun parrainage pour le moment.</div>}
          {data.referrals.map((r) => (
            <div key={r.referral_id} data-testid={`referral-row-${r.referral_id}`} className="grid grid-cols-12 items-center bg-white border-b border-r border-border text-sm">
              <div className="col-span-4 px-4 py-3 border-r border-border">{r.referrer_name}</div>
              <div className="col-span-4 px-4 py-3 border-r border-border">{r.referee_name || r.referee_email}</div>
              <div className="col-span-2 px-4 py-3 border-r border-border">
                <span className={`text-xs font-medium ${r.status === "rewarded" ? "text-sage" : "text-obsidian/40"}`}>{r.status === "rewarded" ? "récompensé" : "en attente"}</span>
              </div>
              <div className="col-span-2 px-4 py-3 text-xs text-obsidian/50 tabnum">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
          ))}
         </div>
        </div>
      </div>
    </CoachLayout>
  );
}

function Counter({ label, value, accent }) {
  return (
    <div className="bg-white border border-border px-5 py-4 min-w-[105px] flex-1">
      <div className="text-[11px] tracking-[0.14em] uppercase text-obsidian/40">{label}</div>
      <div className={`mt-1 font-display text-3xl font-medium tabnum ${accent ? "text-sage" : ""}`}>{value}</div>
    </div>
  );
}
